import { Controller } from '@hotwired/stimulus'

// Pending submits by the node they are anchored to (see `#anchor`), shared by
// every instance: the form that replaces this one gets a controller of its own.
// An entry leaves when its timer fires or is cleared.
const pendingSubmits = new Map()

// A page render ends the page's pending submits. A frame render keeps them, that
// being the swap they follow, but a full render can carry the same permanent field
// into another page's form: `q` and `contentSearch` recur across the search headers.
document.addEventListener('turbo:before-render', () => {
  for (const timeout of pendingSubmits.values()) clearTimeout(timeout)
  pendingSubmits.clear()
})

// Submits the form after its fields change, debounced by `delay` ms (0 submits at once).
//
// Replaces @stimulus-components/auto-submit, whose pending submit outlived the form.
// The search headers sit inside the turbo-frame their results replace, and only the
// `data-turbo-permanent` input survives the swap. A keystroke made while the previous
// search was still loading fired on the detached old form: the browser cancelled it
// ("Form submission canceled because the form is not connected") and the results
// stopped a keystroke short of the box. So a pending submit belongs to the node that
// survives the swap, and goes to whichever form holds that node when it fires.
export default class AutoSubmitController extends Controller {
  static values = {
    delay: { type: Number, default: 150 },
    // The form to submit when the fields reach it through `form=` instead of nesting.
    form: String
  }

  submit () {
    const anchor = this.#anchor
    const formId = this.formValue

    clearTimeout(pendingSubmits.get(anchor))
    if (this.delayValue > 0) {
      pendingSubmits.set(anchor, setTimeout(() => {
        pendingSubmits.delete(anchor)
        submitFormOf(anchor, formId)
      }, this.delayValue))
    } else {
      submitFormOf(anchor, formId)
    }
  }

  // The permanent field Turbo carries into the replacing form, or the form itself
  // when nothing is carried — then a replaced form takes its pending submit with it.
  get #anchor () {
    return this.element.querySelector('[id][data-turbo-permanent]') || this.element
  }
}

function submitFormOf (anchor, formId) {
  const form = formId ? document.getElementById(formId) : anchor.closest('form')
  if (form?.isConnected) form.requestSubmit()
}
